<script setup>
import { computed } from 'vue';
import TeamCard from '@/components/teams/TeamCard.vue';

// Match summary beside (scorer) or below (viewer) the live score: how the match got to
// the current score. Content adapts to the scoring system:
//  - unit progression tables (sets/games/frames/legs/ends) from completedUnits + the
//    in-play unit, with scores where they read naturally and winner-only rows where raw
//    counts would mislead (tennis games, darts legs)
//  - break & foul play-by-play for accumulator systems (snooker/pool)
//  - highlights from schema events (180s, checkouts, aces, hot ends)
const props = defineProps({
  state: { type: Object, default: null },
  schema: { type: Object, default: null },
  homeName: { type: String, default: 'Home' },
  awayName: { type: String, default: 'Away' },
  homeTeam: { type: Object, default: null }, // fixture teams: rendered via team-card
  awayTeam: { type: Object, default: null },
});

const sideName = (side) => (side === 'home' ? props.homeName : props.awayName);
const sideTeam = (side) => (side === 'home' ? props.homeTeam : props.awayTeam);

// unit chain below the root, in play order (match > set > game)
const unitChain = computed(() => {
  const units = props.schema?.units ?? {};
  const chain = [];
  let name = units[props.schema?.rootUnit]?.child;
  while (name && units[name]) {
    chain.push({ name, unit: units[name] });
    name = units[name].child;
  }
  return chain;
});

const counterFor = (unit) =>
    unit?.scoredBy ? props.schema?.counters?.[unit.scoredBy] : null;

// raw counts mislead where the score has its own language (tennis points, darts legs)
const winnerOnly = (unit) =>
    counterFor(unit)?.type === 'countdown' ||
    (unit.scoreDisplay && unit.scoreDisplay !== 'perSide' && unit.scoreDisplay !== 'aggregate');

const sections = computed(() => {
  if (!props.state) return [];
  const units = props.schema?.units ?? {};
  const rootName = props.schema?.rootUnit;
  return unitChain.value
      .map(({ name, unit }, depth) => {
        const completed = props.state.completedUnits?.[name] ?? [];
        // the in-play instance, straight from the path (skip untouched 0-0 rows)
        const current = props.state.path?.find(
            (u) => u.unit === name && (u.home > 0 || u.away > 0));
        // the engine's index restarts per parent (Game 1..6 within each set)
        const rows = completed.map((u, i) => ({
          key: `${name}-${i}`,
          label: `${unit.label ?? name} ${u.index ?? i + 1}`,
          score: u,
          inPlay: false,
        }));
        if (current && !props.state.completed) {
          rows.push({
            key: `${name}-current`,
            label: `${unit.label ?? name} ${current.index ?? completed.length + 1}`,
            score: current,
            inPlay: true,
          });
        }

        // nested units (tennis games under sets) split into one card per parent
        // instance, headed "Set 1", "Set 2"... — an index restart marks a new parent
        const parentName = depth === 0 ? rootName : unitChain.value[depth - 1].name;
        const grouped = parentName !== rootName;
        const parentLabel = units[parentName]?.label ?? parentName;
        const groups = [];
        let prevIndex = 0;
        for (const row of rows) {
          const index = row.score.index ?? prevIndex + 1;
          if (groups.length === 0 || (grouped && index <= prevIndex)) groups.push([]);
          groups[groups.length - 1].push(row);
          prevIndex = index;
        }
        return {
          name,
          winnerOnly: winnerOnly(unit),
          cards: groups.map((groupRows, gi) => ({
            key: `${name}-g${gi}`,
            label: grouped ? `${parentLabel} ${gi + 1}` : null,
            rows: groupRows,
          })),
        };
      })
      .filter((s) => s.cards.some((c) => c.rows.length > 0));
});

// break & foul play-by-play (accumulator systems), most recent last
const historyRows = computed(() =>
    (props.state?.history ?? []).map((h, i) => ({
      key: `h-${i}`,
      side: h.side,
      text: h.type === 'break' ? `Break ${h.value}` : `Foul +${h.value}`,
      isFoul: h.type === 'foul',
      unitLabel: unitRefLabel(h.unitRef),
    })));

const unitRefLabel = (unitRef) => {
  // "frame:2" -> "Frame 2" (only worth showing once there is more than one)
  const last = unitRef?.split('/').pop();
  if (!last) return null;
  const [name, index] = last.split(':');
  const label = props.schema?.units?.[name]?.label ?? name;
  return `${label} ${index}`;
};

// consecutive rows from the same frame/leg sit under one "Frame 1" card
const eventGroups = computed(() => {
  const groups = [];
  for (const row of historyRows.value) {
    const label = row.unitLabel ?? '';
    const last = groups[groups.length - 1];
    if (!last || last.label !== label) groups.push({ label, rows: [row] });
    else last.rows.push(row);
  }
  return groups;
});

// highlights (aces, double faults, 180s...) aggregate to TOTALS per team — individual
// occurrences aren't the story; the count is. A best value tags along where one exists
// (high checkout 121). Suppressed when the play-by-play already tells it (snooker breaks).
const highlightTotals = computed(() => {
  if (historyRows.value.length > 0) return [];
  const defs = Object.fromEntries((props.schema?.events ?? []).map((e) => [e.key, e]));
  const byKey = new Map();
  for (const e of props.state?.events ?? []) {
    const def = defs[e.key];
    if (!def || (e.side !== 'home' && e.side !== 'away')) continue;
    let entry = byKey.get(e.key);
    if (!entry) {
      entry = { key: e.key, label: def.label, home: [], away: [] };
      byKey.set(e.key, entry);
    }
    entry[e.side].push(e.value ?? null);
  }
  const cell = (values) => {
    if (values.length === 0) return '0';
    const best = Math.max(...values.filter((v) => v != null));
    return Number.isFinite(best) && best > 1 && best !== values.length
        ? `${values.length} · best ${best}`
        : String(values.length);
  };
  return [...byKey.values()].map((entry) => ({
    key: entry.key,
    label: entry.label,
    home: cell(entry.home),
    away: cell(entry.away),
  }));
});

const hasContent = computed(() =>
    sections.value.length > 0 || historyRows.value.length > 0 || highlightTotals.value.length > 0);
</script>

<template>
  <div class="ms">
    <div v-if="!hasContent" class="text-body-2 text-medium-emphasis">
      Nothing to show yet. The summary builds as the match is scored.
    </div>

    <template v-else>
      <!-- unit progression: rows label themselves (Frame 1, Set 2...); nested units get
           one card per parent (games split into their sets) -->
      <template v-for="section in sections" :key="section.name">
        <div v-for="card in section.cards" :key="card.key" class="ms__card">
          <div v-if="card.label" class="ms__eyebrow">{{ card.label }}</div>
          <table class="ms__table">
            <thead v-if="!section.winnerOnly">
              <tr>
                <th />
                <th class="ms__th-team">
                  <div class="d-flex justify-center">
                    <team-card v-if="homeTeam" :team="homeTeam" size="small" :minimized="true"
                               :expandable="false" :show-squash-level="false" />
                    <template v-else>{{ homeName }}</template>
                  </div>
                </th>
                <th class="ms__th-team">
                  <div class="d-flex justify-center">
                    <team-card v-if="awayTeam" :team="awayTeam" size="small" :minimized="true"
                               :expandable="false" :show-squash-level="false" />
                    <template v-else>{{ awayName }}</template>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in card.rows" :key="row.key" :class="{'ms__row--live': row.inPlay}">
                <td class="ms__cell-label">
                  {{ row.label }}
                  <span v-if="row.inPlay" class="ms__live-dot" />
                </td>
                <template v-if="section.winnerOnly && !row.inPlay">
                  <td colspan="2" class="ms__cell-winner">{{ sideName(row.score.winnerSide) }}</td>
                </template>
                <template v-else>
                  <td class="ms__cell-score" :class="{'ms__cell-score--won': row.score.winnerSide === 'home'}">
                    {{ row.score.homeDisplay ?? row.score.home }}
                  </td>
                  <td class="ms__cell-score" :class="{'ms__cell-score--won': row.score.winnerSide === 'away'}">
                    {{ row.score.awayDisplay ?? row.score.away }}
                  </td>
                </template>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <!-- breaks/fouls play-by-play: one bordered card per frame -->
      <div v-for="group in eventGroups" :key="group.label || 'all'" class="ms__card">
        <div class="ms__eyebrow">{{ group.label || 'Events' }}</div>
        <table class="ms__table">
          <tbody>
            <tr v-for="row in group.rows" :key="row.key">
              <td class="ms__cell-team">
                <team-card
                  v-if="sideTeam(row.side)"
                  :team="sideTeam(row.side)"
                  size="small"
                  :expandable="false"
                  :show-squash-level="false"
                  :max-lines="1"
                />
                <template v-else>{{ row.side ? sideName(row.side) : '' }}</template>
              </td>
              <td class="ms__cell-event" :class="{'ms__cell-event--foul': row.isFoul}">
                {{ row.text }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- highlight totals per team (aces, double faults, 180s...) -->
      <div v-if="highlightTotals.length" class="ms__card">
        <table class="ms__table">
          <thead>
            <tr>
              <th />
              <th class="ms__th-team">
                <div class="d-flex justify-center">
                  <team-card v-if="homeTeam" :team="homeTeam" size="small" :minimized="true"
                             :expandable="false" :show-squash-level="false" />
                  <template v-else>{{ homeName }}</template>
                </div>
              </th>
              <th class="ms__th-team">
                <div class="d-flex justify-center">
                  <team-card v-if="awayTeam" :team="awayTeam" size="small" :minimized="true"
                             :expandable="false" :show-squash-level="false" />
                  <template v-else>{{ awayName }}</template>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in highlightTotals" :key="row.key">
              <td class="ms__cell-label">{{ row.label }}</td>
              <td class="ms__cell-score">{{ row.home }}</td>
              <td class="ms__cell-score">{{ row.away }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<style scoped>
/* each section (progression table, frame events) is its own hairline card;
   x padding lives on the cells so row dividers run border to border */
.ms__card {
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 14px;
  padding: 8px 0;
  background: rgb(var(--v-theme-surface));
  margin-top: 12px;
  overflow: hidden;
}
.ms__card:first-of-type {
  margin-top: 0;
}

.ms__eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(var(--v-theme-on-surface), 0.55);
  margin: 4px 16px 6px;
}

.ms__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.ms__table td {
  padding: 9px 8px;
  border-top: 1px solid rgb(var(--v-theme-borderLight));
}
.ms__table td:first-child {
  padding-left: 16px;
}
.ms__table td:last-child {
  padding-right: 16px;
}
.ms__table tr:first-child td {
  border-top: none;
}

.ms__cell-label {
  color: rgba(var(--v-theme-on-surface), 0.7);
}
.ms__cell-score {
  text-align: center;
  width: 64px;
  font-variant-numeric: tabular-nums;
}
.ms__cell-score--won {
  font-weight: 700;
}
.ms__cell-winner {
  text-align: right;
  font-weight: 600;
}

.ms__row--live .ms__cell-label {
  color: rgb(var(--v-theme-on-surface));
  font-weight: 600;
}
.ms__live-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgb(var(--v-theme-primary));
  margin-left: 4px;
  vertical-align: middle;
}

.ms__cell-team {
  max-width: 0; /* let the team-card truncate instead of stretching the column */
  width: 60%;
}
.ms__cell-event {
  text-align: right;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.ms__cell-event--foul {
  color: rgb(var(--v-theme-error));
}
.ms__th-team {
  width: 64px;
  padding: 4px 8px 6px;
}
.ms__th-team:last-child {
  padding-right: 16px;
}
</style>
